import { useState } from 'react';
import { AnyAction } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from 'src/store';
import {
  CardBody,
  CardContainer,
  Divider,
  Switch,
  TextInput,
} from 'src/components/atoms';
import { ShadowsSize } from 'src/components/foundations/tokens';
import {
  editPersonalInformation,
  updateAvailability,
  updateTranslationEnabled,
} from 'src/creators';
import { useIsFeatureEnabled } from 'src/hooks';
import * as Types from '../../../types';
import {
  DividerContainer,
  LeftSide,
  RightSide,
  StyledContainer,
  StyledHeader,
  StyledSubDescription,
  StyledSubHeader,
  StyledSubSection,
} from './CaseSettings.styles';

export const CaseSettings = () => {
  const { currentUser } = useSelector(
    ({ globalUsers: { currentUser } }: Types.State) => ({
      currentUser,
    }),
  );
  const [displayName, setDisplayName] = useState(
    currentUser?.displayName || currentUser?.name || '',
  );
  const dispatch = useDispatch<AppDispatch>();

  const handleAvailabilityToggle = async (newToggleValue: boolean) => {
    dispatch(updateAvailability(newToggleValue) as unknown as AnyAction);
  };

  const handleTranslationToggle = async (newToggleValue: boolean) => {
    dispatch(updateTranslationEnabled(newToggleValue) as unknown as AnyAction);
  };

  const updateDisplayName = () => {
    const allowEditingUser =
      displayName !== (currentUser?.displayName || currentUser?.name);

    if (allowEditingUser) {
      const { eid, email, name, role, site, department, country } =
        currentUser!;

      dispatch(
        editPersonalInformation({
          eid,
          email,
          name,
          role,
          site,
          department,
          country,
          displayName,
        }),
      );
    }
  };

  const handleDisplayNameChange = async (name: string) => {
    setDisplayName(name);
  };

  const isDynamicTranslationsEnabled = useIsFeatureEnabled(
    Types.FeatureFlag.DYNAMIC_TRANSLATIONS,
  );

  return (
    <StyledContainer data-testid="case-settings-page">
      <StyledHeader>Case Settings</StyledHeader>
      <CardContainer $shadowSize={ShadowsSize.large}>
        <CardBody>
          <StyledSubSection>
            <LeftSide>
              <StyledSubHeader>Case manager availability</StyledSubHeader>
              <StyledSubDescription>
                When this option is enabled, you’ll be able to be assigned new
                reports coming into the Resolution Hub. <br /> This will not
                impact your ability to be assignable as a case manager on
                reports or cases already in Vault.
              </StyledSubDescription>
            </LeftSide>
            <RightSide>
              <Switch
                label={`Availability ${
                  currentUser?.available ? 'enabled' : 'disabled'
                }`}
                data-testid="report-availability-toggle"
                checked={currentUser?.available ?? false}
                onClick={() =>
                  handleAvailabilityToggle(!currentUser?.available)
                }
              />
            </RightSide>
          </StyledSubSection>
        </CardBody>
        <DividerContainer>
          <Divider variant="horizontal" />
        </DividerContainer>
        <CardBody>
          <StyledSubSection>
            <LeftSide>
              <StyledSubHeader>Your display name</StyledSubHeader>
              <StyledSubDescription>
                Your display name will be seen by reporters when they submit a
                report that&apos;s assigned to you.
              </StyledSubDescription>
            </LeftSide>
            <RightSide>
              <TextInput
                state={Types.FormState.DEFAULT}
                data-testid="edit-display-name-input"
                placeholder="Your display name"
                value={displayName}
                type="text"
                onChange={(event) =>
                  handleDisplayNameChange(event.target.value)
                }
                onBlur={() => updateDisplayName()}
              ></TextInput>
            </RightSide>
          </StyledSubSection>
        </CardBody>
        {isDynamicTranslationsEnabled && (
          <>
            <DividerContainer>
              <Divider variant="horizontal" />
            </DividerContainer>
            <CardBody>
              <StyledSubSection>
                <LeftSide>
                  <StyledSubHeader>
                    Enable auto-translations for reporter messages
                  </StyledSubHeader>
                  <StyledSubDescription>
                    Enable auto-translations and read all reporter messages in
                    your preferred language.
                    <br />
                    The translated language is based on the language selected on
                    your device.
                    <br />
                    <br />
                    You can switch between the translated and original language
                    in each reporter chat.
                  </StyledSubDescription>
                </LeftSide>
                <RightSide>
                  <Switch
                    label={`Translation ${
                      currentUser?.translationEnabled ? 'enabled' : 'disabled'
                    }`}
                    data-testid="report-translation-toggle"
                    checked={currentUser?.translationEnabled ?? false}
                    onClick={() =>
                      handleTranslationToggle(!currentUser?.translationEnabled)
                    }
                  />
                </RightSide>
              </StyledSubSection>
            </CardBody>
          </>
        )}
      </CardContainer>
    </StyledContainer>
  );
};
